import { useEffect, useState, useCallback } from 'react';
import { format, subDays } from 'date-fns';

const STORAGE_KEY = 'pomodoro-history';
const HISTORY_EVENT = 'pomodoro-history-updated';

type DayEntry = { count: number; totalMinutes: number };
type HistoryMap = Record<string, DayEntry>;

function readHistory(): HistoryMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeHistory(history: HistoryMap) {
  // Keep only last 14 days to avoid unbounded growth
  const cutoff = format(subDays(new Date(), 13), 'yyyy-MM-dd');
  const trimmed: HistoryMap = {};
  for (const [date, entry] of Object.entries(history)) {
    if (date >= cutoff) trimmed[date] = entry;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  window.dispatchEvent(new CustomEvent(HISTORY_EVENT));
}

/**
 * Records a completed Pomodoro focus session. Safe to call from non-React code.
 */
export function recordPomodoro(minutes: number) {
  if (!minutes || minutes <= 0) return;
  const today = format(new Date(), 'yyyy-MM-dd');
  const history = readHistory();
  const existing = history[today] ?? { count: 0, totalMinutes: 0 };
  history[today] = {
    count: existing.count + 1,
    totalMinutes: existing.totalMinutes + minutes,
  };
  writeHistory(history);
}

export interface DayStat {
  date: string;
  label: string;
  count: number;
  minutes: number;
  isToday: boolean;
}

export function usePomodoroHistory() {
  const [history, setHistory] = useState<HistoryMap>(() => readHistory());

  useEffect(() => {
    const refresh = () => setHistory(readHistory());
    window.addEventListener(HISTORY_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(HISTORY_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const today = history[todayKey] ?? { count: 0, totalMinutes: 0 };

  const last7Days: DayStat[] = Array.from({ length: 7 }).map((_, i) => {
    const date = subDays(new Date(), 6 - i);
    const key = format(date, 'yyyy-MM-dd');
    const entry = history[key] ?? { count: 0, totalMinutes: 0 };
    return {
      date: key,
      label: format(date, 'EEE').charAt(0).toUpperCase(),
      count: entry.count,
      minutes: entry.totalMinutes,
      isToday: i === 6,
    };
  });

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(HISTORY_EVENT));
  }, []);

  return {
    todayCount: today.count,
    todayMinutes: today.totalMinutes,
    last7Days,
    reset,
  };
}
